import {FeatureErrorBoundary} from '@hconnect/common/components'
import {FeatureTranslationsLoader} from '@hconnect/common/localization-loaders'
import {useFundamentalMuiTheme} from '@hconnect/uikit'
import {useIsHierarchyPickerSwitch, useUpmPlantFromHierarchyId} from '@hproduce/suite-core'
import {CircularProgress, ThemeProvider} from '@mui/material'
import {LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import {Suspense} from 'react'
import {Navigate, Outlet, RouteObject, useLocation, useResolvedPath} from 'react-router'

import {STOPPAGE_ANALYSIS, EQUIPMENT_SUB_PATH, RouteSegment} from '../consts'
import {stoppageAnalysisFeatureTranslations} from '../localization'
import {DefaultEquipmentRedirect, StoppageAnalysisPage} from '../pages'

const SuiteDefaultEquipmentRedirect = () => {
  const plantId = useUpmPlantFromHierarchyId()?.plantId

  if (!plantId) {
    return null
  }

  return <DefaultEquipmentRedirect plantId={plantId} />
}

const SuiteDefaultEquipmentRedirectWrapper = () => {
  return (
    <ThemeProvider theme={useFundamentalMuiTheme()}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Suspense fallback={<CircularProgress color="secondary" size={80} />}>
          <SuiteDefaultEquipmentRedirect />
        </Suspense>
      </LocalizationProvider>
    </ThemeProvider>
  )
}

const SuiteStoppageAnalysisPage = () => {
  const plantId = useUpmPlantFromHierarchyId()?.plantId

  if (!plantId) {
    return null
  }

  return <StoppageAnalysisPage plantId={plantId} />
}

const SuiteStoppageAnalysisRouteWrapper = () => {
  return (
    <ThemeProvider theme={useFundamentalMuiTheme()}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Suspense fallback={<CircularProgress color="secondary" size={80} />}>
          <SuiteStoppageAnalysisPage />
        </Suspense>
      </LocalizationProvider>
    </ThemeProvider>
  )
}

/**
 * Stoppage Analysis embedded in another feature (e.g. a dashboard) only exists for plants: other
 * hierarchy nodes go back to the host page. A picker switch to another plant drops the equipment of
 * the previous plant, so the default equipment of the new plant opens.
 */
const EmbeddedStoppageAnalysisGuard = () => {
  const plant = useUpmPlantFromHierarchyId()
  const isPickerSwitch = useIsHierarchyPickerSwitch()
  const {pathname, search} = useLocation()
  const hostUrl = useResolvedPath('..').pathname
  const stoppageAnalysisUrl = useResolvedPath('.').pathname

  if (plant === undefined) {
    return <CircularProgress color="secondary" size={80} />
  }

  if (!plant) {
    return <Navigate replace to={`${hostUrl}${search}`} />
  }

  if (isPickerSwitch && pathname.replace(/\/+$/, '') !== stoppageAnalysisUrl) {
    return <Navigate replace to={`${stoppageAnalysisUrl}${search}`} />
  }

  return <Outlet />
}

/**
 * A composable sub-route for the stoppage analysis page.
 *
 * Designed to be nested inside any parent route whose page navigates to
 * `STOPPAGE_ANALYSIS_SUB_PATH` (e.g. onEquipmentClick in running-times).
 * By embedding this sub-route, the stoppage analysis page becomes a child of the caller's
 * route tree, which preserves "back to" navigation and allows seamless integration
 * into other features without duplicating route definitions.
 *
 * Includes its own `FeatureTranslationsLoader` so it's self-contained — translations
 * are loaded regardless of whether the parent feature loads them.
 */
export const stoppageAnalysisSubRoute: RouteObject = {
  path: STOPPAGE_ANALYSIS,
  element: (
    <FeatureTranslationsLoader translations={stoppageAnalysisFeatureTranslations}>
      <FeatureErrorBoundary>
        <EmbeddedStoppageAnalysisGuard />
      </FeatureErrorBoundary>
    </FeatureTranslationsLoader>
  ),
  children: [
    {
      index: true,
      element: <SuiteDefaultEquipmentRedirectWrapper />
    },
    {
      path: RouteSegment.EQUIPMENT,
      element: <Navigate to=".." replace />
    },
    {
      path: EQUIPMENT_SUB_PATH,
      element: <SuiteStoppageAnalysisRouteWrapper />
    }
  ]
}

export const stoppageAnalysisRoutes: RouteObject = {
  path: '*',
  element: (
    <FeatureTranslationsLoader translations={stoppageAnalysisFeatureTranslations}>
      <Outlet />
    </FeatureTranslationsLoader>
  ),
  children: [
    {
      index: true,
      element: <SuiteDefaultEquipmentRedirectWrapper />
    },
    {
      path: RouteSegment.EQUIPMENT,
      element: <Navigate to=".." replace />
    },
    {
      path: EQUIPMENT_SUB_PATH,
      element: <SuiteStoppageAnalysisRouteWrapper />
    }
  ]
}
